import { Checkbox as CheckboxPrimitive } from 'radix-ui';
import * as React from 'react';

import { Icon } from './Icon';
// Type-only import so the `{@link Switch}` JSDoc reference below resolves.
// eslint-disable-next-line unused-imports/no-unused-imports, @typescript-eslint/no-unused-vars
import { type Switch } from './Switch';
import { cn } from './utils/cn';

/**
 * Tri-state boolean input (`unchecked`, `checked`, `indeterminate`) used in
 * forms and multi-select lists. Pair with a label and an `aria-label`
 * so the meaning of each state is clear to assistive tech.
 *
 * For settings whose value takes effect the moment the user toggles them,
 * prefer {@link Switch}.
 *
 * @example
 * <Checkbox
 *   checked={isAcknowledged}
 *   onCheckedChange={setIsAcknowledged}
 *   aria-label="I accept the terms"
 * />
 */
function Checkbox({
  className,
  ...props
}: React.ComponentProps<typeof CheckboxPrimitive.Root>) {
  return (
    <CheckboxPrimitive.Root
      data-slot="checkbox"
      className={cn(
        'peer relative flex size-4 shrink-0 items-center justify-center rounded-sm border border-border-tertiary transition-colors outline-none group-has-disabled/field:opacity-50 after:absolute after:-inset-x-3 after:-inset-y-2 focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20 aria-invalid:aria-checked:border-primary dark:bg-input/30 dark:aria-invalid:border-destructive/50 dark:aria-invalid:ring-destructive/40 data-checked:border-primary data-checked:bg-primary data-checked:text-primary-foreground dark:data-checked:bg-primary',
        className,
      )}
      {...props}
    >
      <CheckboxPrimitive.Indicator
        data-slot="checkbox-indicator"
        className="grid place-content-center text-current transition-none"
      >
        <Icon
          name={props.checked === 'indeterminate' ? 'Minus' : 'Check'}
          size="sm"
        />
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );
}

export { Checkbox };
